import {initDebugger} from "../../støttefunksjoner/debug";
import {Injectable} from "@angular/core";
import {Appkonfigurasjon} from "../Appkonfigurasjon";
import {CriiptoGuiModus, CriiptoIdTilbyder, DokumentAnnoteringId, DokumentId, DokumentVersjonModell, EnkelBrukerModell, EnkelGruppeModell, GruppeId, GruppeMedlemListeModell, HttpsUrl, MøteId, MøteModellEnkel, Signeringsmetode, SpørreundersøkelseEnkelModell} from "./generertNg2HttpKlient";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {somSikkertFilnavnKunAscii} from "felles-ng2/støttefunksjoner/filer";
import {Temporal} from "temporal-polyfill";
import {uriEnkodet} from "felles-ng2/støttefunksjoner/uri";
import {skrivZonedDateTimeSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {mimetyper} from "felles-ng2/støttefunksjoner/mimetyper";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {brukPdfJsLegacy} from "felles-ng2/støttefunksjoner/pdfjs";
import {AgendaelementModellDLM} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import ZonedDateTime = Temporal.ZonedDateTime;

const debug = initDebugger('export class RessursAdresser {\n');

/** Adresser til ressurser som ikke er definert i src/scripts/felles-ng2/tjenester/ressurser/generertNg2HttpKlient.ts.
 * Se også {@link EksterneLenker} og {@link BildeRessurser}  */
@Injectable({providedIn: 'root'})
export class RessursAdresser {

    constructor(private readonly konfigurasjon: Appkonfigurasjon, private oversettelser: Oversettelser) {
    }

    /** Returnerer url som redirigerer klient til url som benyttes for å vise criipto signeringsgui.
     * Bør normalt foretrekkes fremfor DokumentsigneringRessursClient.signerUrl(..) da vi her unngår problemet med at popup bli blokkert som følge av promise. */
    criiptoSigneringUrl(dokumentId: DokumentId, signeringsmetode: Signeringsmetode): string {
        return `${this.serviceRotUrl()}/api/dokument/signering/${dokumentId}/signerUrl/${signeringsmetode}?redirect=true`;
    }

    /** Returnerer url som redirigerer klient til url som benyttes for å vise openid autentiserings-gui.
     * Bør normalt foretrekkes fremfor CriiptoOIDCRessursClient.authorizeUrl(..) da vi her unngår problemet med at popup bli blokkert som følge av promise. */
    criiptoAutentiseringUrl(guimodus: CriiptoGuiModus, tilbyder: CriiptoIdTilbyder): string {
        return `${this.serviceRotUrl()}/api/autentisering/criipto/authorizeUrl/${guimodus}/${tilbyder}?redirect=true`;
    }

    /** Returnerer url for nedlasting av protokoll fra angitte møter i en ZIP.
     * Samme URL som {@link MøteRessursClient#eksporterProtokoller} */
    protokollEksportZipUrl(møteIder: MøteId[]): HttpsUrl {
        const filnavn = somSikkertFilnavnKunAscii(this.oversettelser.oversett(i18n('protokoller.zip')));
        return `${this.serviceRotUrl()}/api/moete/eksporterProtokoller/${filnavn}?id=${møteIder.join('&id=')}`;
    }

    /**
     * Returnerer url for nedlasting av protokoll i PDF-format.
     * Samme URL som {@link MøteRessursClient#lastNedProtokolldokument}
     * MERK filnavn bør holdes i synk med no.systemfabrikken.lederplan.domene.dokument.DokumentVersjon.GenerertProtokollDokumentVersjon.filnavn
     */
    protokollPdfUrl(møte: Møteinformasjon): string {
        const filnavn = somSikkertFilnavnKunAscii(this.oversettelser.oversett(i18n('protokoll-{nummer}.pdf'), {nummer: møte.nummer}));
        return `${this.serviceRotUrl()}/api/moete/${møte.id}/protokoll/pdf/${filnavn}`;
    }

    brukerAvatarUrl(bruker: Pick<EnkelBrukerModell, 'id' | 'avatarId'>): HttpsUrl {
        return `/api/bruker/${bruker.id}/avatar/${bruker.avatarId}.jpg`;
    }

    medlemAvatarUrl(medlem: Pick<GruppeMedlemListeModell, 'id' | 'avatarId'>): HttpsUrl {
        return `${this.serviceRotUrl()}/api/medlem/${medlem.id}/avatar/${medlem.avatarId}.jpg`;
    }

    gruppelogoUrl(gruppe: Pick<EnkelGruppeModell, 'id' | 'logoId'>, bredde = 40): HttpsUrl {
        return `${this.serviceRotUrl()}/api/gruppe/${gruppe.id}/logo/${gruppe.logoId}?format=png&bredde=${bredde}`;
    }

    eksporterMøteDeltakelseUrl(gruppeId: GruppeId, fra: ZonedDateTime, til: ZonedDateTime): HttpsUrl {
        return `${this.serviceRotUrl()}/api/gruppe/${gruppeId}/moetedeltakelse.xlsx?fom=${uriEnkodet(skrivZonedDateTimeSerialisert(fra))}&tom=${uriEnkodet(skrivZonedDateTimeSerialisert(til))}`;
    }

    spørreundersøkelsePdfRapportUrl(undersøkelse: SpørreundersøkelseEnkelModell): HttpsUrl {
        const filnavn = somSikkertFilnavnKunAscii(this.oversettelser.oversett(i18n('{tittel}-resultater.pdf'), {tittel: undersøkelse.tittel}));
        return `${this.serviceRotUrl()}/api/spoerreundersoekelse/${undersøkelse.id}/pdfRapport/${filnavn}`;
    }

    /** Returnerer URL som kan brukes for å vise angitt dokumentversjon i vår tilpassede PDFjs-viser */
    pdfjsDokumentViserUrl(dokumentversjon: DokumentVersjonModell, forespurtAnnoteringId?: DokumentAnnoteringId): HttpsUrl {
        //vi bygger to versjoner av pdf.js - en for moderne nettlesere og en for eldre - sjekker her hvilken vi skal servere
        const legacy = brukPdfJsLegacy();
        let url = `${this.serviceRotUrl()}/app/visdokument/pdf${legacy ? '-legacy' : ''}/versjon/${dokumentversjon.id}/${uriEnkodet(mimetyper.pdf.mimetype)}/${uriEnkodet(dokumentversjon.pdfFilnavn!)}`;
        if (erIkkeNil(forespurtAnnoteringId)) url = `${url}?forespurtAnnoteringId=${forespurtAnnoteringId}`;
        return url;
    }

    /** Returnerer url som angitt dokument kan lastes ned via */
     dokumentUrl(dokumentVersjonId: string, mimetype: string, filnavn: string): HttpsUrl { //@todo gjøre filnavn optional? - hvordan takler serversiden det?
        return `${this.serviceRotUrl()}/api/dokument/data/${dokumentVersjonId}/${uriEnkodet(mimetype)}/${uriEnkodet(filnavn)}`;
    }

    /** Returnerer url til angitt dokumentversjons forsidebilde  */
    dokumentForsideUrl(dokumentversjon: DokumentVersjonModell): HttpsUrl {
        return `${this.serviceRotUrl()}/api/dokument/${dokumentversjon.id}/forside?format=png&bredde=130`
    }
    //private hjelpemetoder

    private serviceRotUrl() {
        return this.konfigurasjon.serviceRotUrl;
    }

    saksdokumentUrl(sak: AgendaelementModellDLM):HttpsUrl {
        const filnavn = somSikkertFilnavnKunAscii(this.oversettelser.oversett(i18n('sak-{saksnummer}.pdf'), {saksnummer: sak.nummer}));
        return `${this.konfigurasjon.serviceRotUrl}/api/sak/${sak.id}/pdf/${filnavn}.pdf`
    }
}

type Møteinformasjon = Pick<MøteModellEnkel, 'id' | 'nummer'>
